import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormikProps } from "formik";

interface ExpertiseNode {
  id: number;
  name: string;
  level: "Beat" | "SubBeat" | "Job Title";
  children?: ExpertiseNode[];
}

interface FlatNode extends ExpertiseNode {
  path: string;
}

interface Props {
  name: string;
  label: string;
  options: ExpertiseNode[];
  formik: FormikProps<any>;
}

const FormikHierarchySelect: React.FC<Props> = ({
  name,
  label,
  options,
  formik,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const inputId = useRef(
    `hierarchy-${Math.random().toString(36).slice(2, 9)}`
  ).current;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const value: number[] = formik.values[name] || [];

  /* ---------------- Close dropdown ---------------- */

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* ---------------- Flatten tree ---------------- */

  const flatNodes = useMemo(() => {
    const list: FlatNode[] = [];

    const walk = (nodes: ExpertiseNode[], parents: string[] = []) => {
      nodes.forEach((node) => {
        const path = [...parents, node.name].join(" › ");

        list.push({
          ...node,
          path,
        });

        if (node.children?.length) {
          walk(node.children, [...parents, node.name]);
        }
      });
    };

    walk(options);

    return list;
  }, [options]);

  const selectedNodes = useMemo(
    () => flatNodes.filter((n) => value.includes(n.id)),
    [flatNodes, value]
  );

  /* ---------------- Search ---------------- */

  const filtered = useMemo(() => {
    if (!search.trim()) return [];

    return flatNodes.filter((node) =>
      node.path.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, flatNodes]);

  /* ---------------- Utilities ---------------- */

  const checked = (id: number) => value.includes(id);

  const getNodeById = (
 id: number,
 nodes: ExpertiseNode[] = options
): ExpertiseNode | null => {
 let found: ExpertiseNode | null = null;

 nodes.some((node) => {
if (node.id === id) {
 found = node;
 return true;
}

if (node.children?.length) {
 const child = getNodeById(id, node.children);
 if (child) {
found = child;
return true;
 }
}

return false;
 });

 return found;
};

  const getDescendantIds = (node: ExpertiseNode): number[] => {
    let ids: number[] = [];

    node.children?.forEach((child) => {
      ids.push(child.id);

      if (child.children?.length) {
        ids = [...ids, ...getDescendantIds(child)];
      }
    });

    return ids;
  };

  const getAncestorIds = (
 id: number,
 nodes: ExpertiseNode[] = options,
 parents: number[] = []
): number[] => {
 let result: number[] = [];

 nodes.some((node) => {
if (node.id === id) {
 result = parents;
 return true;
}

if (node.children?.length) {
 const childResult = getAncestorIds(id, node.children, [
...parents,
node.id,
 ]);

 if (childResult.length) {
result = childResult;
return true;
 }
}

return false;
 });

 return result;
};

  /* ---------------- Recursive Selection ---------------- */

  /* const toggleSelect = (node: ExpertiseNode) => {
    const descendants = getDescendantIds(node);
    const family = [node.id, ...descendants];

    const isSelected = checked(node.id);

    let updated = [...value];

    if (isSelected) {
      updated = updated.filter((id) => !family.includes(id));
    } else {
      updated = [...new Set([...updated, ...family])];
    }

    const ancestors = getAncestorIds(node.id);

    ancestors.reverse().forEach((parentId) => {
      const parent = getNodeById(parentId);

      if (!parent) return;

      const childIds = parent.children?.map((c) => c.id) || [];
      const allChildrenSelected = childIds.every((id) =>
        updated.includes(id)
      );

      if (allChildrenSelected) {
        if (!updated.includes(parentId)) updated.push(parentId);
      } else {
        updated = updated.filter((id) => id !== parentId);
      }
    });

    formik.setFieldValue(name, [...new Set(updated)]);
  }; 
 
  const toggleSelect = (
 node: ExpertiseNode,
 includeChildren = true
) => {
 const descendants = includeChildren
? getDescendantIds(node)
: [];

 const family = [node.id, ...descendants];

 let updated = [...value];

 if (checked(node.id)) {
updated = updated.filter((id) => !family.includes(id));
 } else {
updated = [...new Set([...updated, ...family])];
 }

 formik.setFieldValue(name, updated);
}; */

  const toggleSelect = (node: ExpertiseNode) => {
	  let updated = [...value];

	  if (checked(node.id)) {
		updated = updated.filter((id) => id !== node.id);
	  } else {
		updated.push(node.id);
	  }

	  formik.setFieldValue(name, [...new Set(updated)]);
	};

  const removeId = (id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();

    const node = getNodeById(id);

    if (!node) return;

    const descendants = getDescendantIds(node);
    const removeIds = [id, ...descendants];

    formik.setFieldValue(
      name,
      value.filter((x) => !removeIds.includes(x))
    );
  };

  const clearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    formik.setFieldValue(name, []);
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /* ---------------- Indeterminate ---------------- */

  const hasSelectedDescendant = (node: ExpertiseNode): boolean => {
    return (
      node.children?.some(
        (child) =>
          checked(child.id) || hasSelectedDescendant(child)
      ) || false
    );
  };

  const isIndeterminate = (node: ExpertiseNode): boolean => {
    return !checked(node.id) && hasSelectedDescendant(node);
  };

  /* ---------------- Bulk Select ---------------- */

  const selectBranch = (
	  node: ExpertiseNode,
	  select: boolean
	) => {
	  const ids = [node.id, ...getDescendantIds(node)];

	  const updated = select
		? [...new Set([...value, ...ids])]
		: value.filter((id) => !ids.includes(id));

	  formik.setFieldValue(name, updated);
	};


  const totalSelected = useMemo(
    () => new Set(selectedNodes.map((n) => n.id)).size,
    [selectedNodes]
  );

  /* ---------------- Group Cards ---------------- */

  const groupedSelections = useMemo(() => {
    const groups: any = {};

    selectedNodes.forEach((item) => {
      const [beat, subBeat, job] = item.path.split(" › ");

      if (!groups[beat]) {
        groups[beat] = {
          beat,
          beatId: flatNodes.find(
            (x) => x.name === beat && x.level === "Beat"
          )?.id,
          subBeats: {},
        };
      }

      if (subBeat) {
        if (!groups[beat].subBeats[subBeat]) {
          groups[beat].subBeats[subBeat] = {
            name: subBeat,
            id: flatNodes.find(
              (x) =>
                x.name === subBeat &&
                x.level === "SubBeat"
            )?.id,
            jobs: [],
          };
        }

        if (job) {
          groups[beat].subBeats[subBeat].jobs.push({
            id: item.id,
            name: job,
          });
        }
      }
    });

    return groups;
  }, [selectedNodes, flatNodes]);
 
  const getFontWeight = (level: ExpertiseNode["level"]) => {
	 switch (level) {
	case "Beat":
	 return 700;
	case "SubBeat":
	 return 600;
	default:
	 return 400;
	 }
	};
 
    /* ---------------- Tree Rendering ---------------- */

  const renderNode = (
    node: ExpertiseNode,
    depth: 0
  ): React.ReactNode => {
    const hasChildren = !!node.children?.length;
    const isOpen = expanded[node.id];

    return (
      <div key={node.id}>
        {/* Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 12px",
            paddingLeft: 12 + depth * 22,
            transition: "background .25s ease",
          }}
        >
          {/* Rotating SVG Chevron */}
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleExpand(node.id)}
              style={{
                width: 22,
                height: 22,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                display: "grid",
                placeItems: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 20 20"
                style={{
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform .25s ease",
                }}
              >
                <path
                  d="M7 4 L13 10 L7 16"
                  fill="none"
                  stroke="#64748B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <span style={{ width: 22 }} />
          )}

          {/* Checkbox with Indeterminate */}
          <input
            ref={(el) => {
              if (el) el.indeterminate = isIndeterminate(node);
            }}
            type="checkbox"
            checked={checked(node.id)}
            onChange={() => toggleSelect(node)}
            style={{
              width: 16,
              height: 16,
              cursor: "pointer",
            }}
          />

          {/* Name */}
          <div
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: getFontWeight(node.level),
              color: "#1F2937",
            }}
          >
            {node.name}
          </div>

          {/* Select All / Deselect All */}
          {hasChildren && (() => {
			  const ids = [node.id, ...getDescendantIds(node)];

			  const totalCount = ids.length;

			  const selectedCount = ids.filter((id) =>
				  value.includes(id)
				).length;

			  const hasSelection = selectedCount > 0;

			  return (
				<button
				  type="button"
				  onClick={(e) => {
					e.stopPropagation();
					selectBranch(node, !hasSelection);
				  }}
				  style={{
					border: "none",
					background: "transparent",
					color: "#94A3B8",
					fontSize: 12,
					fontWeight: 600,
					cursor: "pointer",
				  }}
				>
				  {selectedCount > 0 ? selectedCount : totalCount}{" "}
				  {hasSelection ? "Deselect all" : "Select all"}
				</button>
			  );
			})()}
        </div>

        {/* Smooth Expand / Collapse */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: isOpen ? "1fr" : "0fr",
            transition: "grid-template-rows .28s ease",
			paddingLeft: 20,
          }}
        >
          <div style={{ overflow: "hidden" }}>
            {node.children?.map((child) =>
              renderNode(child, depth + 1)
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ---------------- Search Result Row ---------------- */

  const renderSearchRow = (item: FlatNode) => (
    <div
      key={item.id}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <input
        type="checkbox"
        checked={checked(item.id)}
        onChange={() => {
          const node = getNodeById(item.id);
          if (node) toggleSelect(node);
        }}
      />

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            color: "#111827",
            marginBottom: 2,
          }}
        >
          {item.name}
        </div>

        <div
          style={{
            fontSize: 11,
            color: "#64748B",
          }}
        >
          {item.path}
        </div>
      </div>
    </div>
  );
 
  const filterTree = (nodes: ExpertiseNode[]): ExpertiseNode[] => {
 if (!search.trim()) return nodes;

 const term = search.toLowerCase();

 return nodes
.map((node) => {
 const children = node.children ? filterTree(node.children) : [];

 const matched = node.name.toLowerCase().includes(term);

 if (matched || children.length) {
return {
 ...node,
 children,
};
 }

 return null;
})
.filter(Boolean) as ExpertiseNode[];
};

const searchTree = useMemo(() => filterTree(options), [search, options]);
 
    /* ---------------- Component ---------------- */

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", width: "100%" }}
    >
      <label
        htmlFor={inputId}
        style={{
          display: "block",
          marginBottom: 8,
          fontWeight: 600,
          color: "#111827",
        }}
      >
        {label}
      </label>

      {/* Trigger */}
      <button
        id={inputId}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="tree"
        aria-expanded={open}
        style={{
          width: "100%",
          minHeight: 54,
          /* maxHeight: 220,
          overflowY: "auto", */
          textAlign: "left",
          cursor: "pointer",
          border: open
            ? "1px solid #2563EB"
            : "1px solid #D1D5DB",
          background: "#FFFFFF",
          padding: "10px 40px 10px 12px",
          position: "relative",
          transition: "all .2s ease",
        }}
      >
        {/* Rotating Chevron */}
        <span
          style={{
            position: "absolute",
            right: 14,
            top: 16,
            transition: "transform .25s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 20 20">
            <path
              d="M5 7 L10 12 L15 7"
              fill="none"
              stroke="#64748B"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        {Object.keys(groupedSelections).length === 0 ? (
          <span style={{ color: "#9CA3AF" }}>
            Select Beat, SubBeat or Job Title
          </span>
        ) : (
          <>
            {/* Selected Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                paddingRight: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#475569",
                }}
              >
                {totalSelected} selected
              </span>

              <span
                onClick={clearAll}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#2563EB",
                  cursor: "pointer",
                }}
              >
                Clear all
              </span>
            </div>

            {/* Group Cards */}
            {Object.values(groupedSelections).map((group: any) => (
              <div
                key={group.beat}
                style={{
                  background: "#ddeaf6",
                  border: "1px solid #BFDBFE",
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 8,
                }}
              >
                {/* Beat */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#2563EB",
                      }}
                    />
                    {group.beat}
                  </div>

                  <span
                    onClick={(e) => removeId(group.beatId, e)}
                    style={{
                      cursor: "pointer",
                      fontWeight: 400,
					  color: "#2F83C9",
					  fontSize: 20,
					  lineHeight: "20px",
                    }}
                  >
                    ×
                  </span>
                </div>

                {/* Sub Beats */}
                {Object.values(group.subBeats).map((sub: any) => (
                  <div
                    key={sub.name}
                    style={{
                      background: "#e9f1f9",
                      borderRadius: 6,
                      padding: 8,
                      marginBottom: 6,
					  border: "1px solid #2f83c933",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#60A5FA",
                          }}
                        />
                        {sub.name}
                      </div>

                      <span
                        onClick={(e) => removeId(sub.id, e)}
                        style={{
                          cursor: "pointer",
                          fontWeight: 400,
						  color: "#5D7896",
						  fontSize: 20,
						  lineHeight: "20px",
                        }}
                      >
                        ×
                      </span>
                    </div>

                    {/* Job Chips */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                      }}
                    >
                      {sub.jobs.map((job: any) => (
                        <div
                          key={job.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "#FFFFFF",
                            border: "1px solid #BFDBFE",
                            borderRadius: 5,
                            padding: "4px 10px",
                            fontSize: 12,
                          }}
                        >
                          {job.name}

                          <span
                            onClick={(e) => removeId(job.id, e)}
                            style={{
                              cursor: "pointer",
                              fontWeight: 400,
                              color: "#5D7896",
							  fontSize: 20,
							  lineHeight: "20px",
                            }}
                          >
                            ×
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </button>

      {/* Animated Dropdown */}
      <div
        style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          left: 0,
          right: 0,
          zIndex: 999,
          background: "#FFFFFF",
          border: "1px solid #D1D5DB",
          borderRadius: 12,
          boxShadow: "0 18px 35px rgba(0,0,0,.15)",
          overflowY: "scroll",
          opacity: open ? 1 : 0,
          transform: open
            ? "translateY(0px)"
            : "translateY(-8px)",
          maxHeight: open ? 300 : 0,
          pointerEvents: open ? "auto" : "none",
          transition:
            "opacity .25s ease, transform .25s ease, max-height .3s ease",
        }}
      >
        {/* Search */}
        <div
			style={{
			position: "relative",
			padding: 12,
			borderBottom: "1px solid #EEF2F7",
			 }}
			>
			 <svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			style={{
			 position: "absolute",
			 left: 22,
			 top: 21,
			}}
			 >
			<circle
			 cx="11"
			 cy="11"
			 r="7"
			 stroke="#94A3B8"
			 strokeWidth="2"
			 fill="none"
			/>
			<path
			 d="M20 20 L17 17"
			 stroke="#94A3B8"
			 strokeWidth="2"
			 strokeLinecap="round"
			/>
			 </svg>

			 <input
			value={search}
			onChange={(e) => setSearch(e.target.value)}
			placeholder="Search Beat, SubBeat or Job Title..."
			style={{
			 width: "100%",
			 padding: "10px 36px 10px 38px",
			 border: "1px solid #D1D5DB",
			 borderRadius: 8,
			 outline: "none",
			}}
			 />

			 {search && (
			<button
			 type="button"
			 onClick={() => setSearch("")}
			 style={{
			position: "absolute",
			right: 22,
			top: 18,
			border: "none",
			background: "transparent",
			cursor: "pointer",
			fontSize: 18,
			color: "#64748B",
			 }}
			>
			 ×
			</button>
			 )}
		</div>

        {/* Scrollable Tree */}
        <div
          style={{
            /* maxHeight: 280,
            overflowY: "auto", */
          }}
        >
          {(search ? searchTree : options).map((node) =>
			 renderNode(node)
			)}
        </div>

       {/* Sticky Footer */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#FFFFFF",
            borderTop: "1px solid #E5E7EB",
            padding: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={clearAll}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #D1D5DB",
              background: "#FFFFFF",
              color: "#374151",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Clear All
          </button>

          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 8,
              border: "none",
              background: "#2563EB",
              color: "#FFFFFF",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>

      {/* Validation Error */}
      {formik.touched[name] && formik.errors[name] && (
        <div
          style={{
            color: "#DC2626",
            fontSize: 12,
            marginTop: 6,
          }}
        >
          {formik.errors[name] as string}
        </div>
      )}
    </div>
  );
};

export default FormikHierarchySelect;